/**
 * Gateway Firmware Manager
 *
 * Dashboard for managing gateway firmware upgrades and whitelist
 * across all companies in SaaS.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import * as XLSX from 'xlsx';
import './styles/GatewayFirmwareManager.css';

// Parse firmware version to extract gateway type and mode
const parseFirmwareVersion = (version) => {
  if (!version) return { type: 'Unknown', mode: 'Unknown', buildDigit: null, parts: null };

  const match = version.match(/^N?(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (!match) return { type: 'Unknown', mode: 'Unknown', buildDigit: null, parts: null };

  const [, modeDigit, major, minor, buildDigit] = match;
  const buildNum = parseInt(buildDigit, 10);
  const modeNum = parseInt(modeDigit, 10);

  let type = 'Unknown';
  if (buildNum >= 0 && buildNum <= 9) type = 'Newton Gateway';
  else if (buildNum >= 10 && buildNum <= 19) type = 'Newton USB Gateway';
  else if (buildNum >= 20 && buildNum <= 29) type = 'Newton PCIe Gateway';
  else if (buildNum >= 30 && buildNum <= 39) type = 'Newton Cisco USB Gateway';

  let mode = 'Unknown';
  if (modeNum === 1) mode = 'On-Prem (Normal)';
  else if (modeNum === 2) mode = 'On-Prem (Service Bus)';
  else if (modeNum === 3) mode = 'On-Prem (RabbitMQ)';
  else if (modeNum === 10) mode = 'SaaS';

  return { type, mode, buildDigit: buildNum, modeDigit: modeNum, parts: [modeNum, parseInt(major), parseInt(minor), buildNum] };
};

// Compare firmware versions
const compareFirmwareVersions = (v1, v2) => {
  const p1 = parseFirmwareVersion(v1);
  const p2 = parseFirmwareVersion(v2);
  if (!p1.parts || !p2.parts) return 0;
  for (let i = 0; i < 4; i++) {
    if (p1.parts[i] < p2.parts[i]) return -1;
    if (p1.parts[i] > p2.parts[i]) return 1;
  }
  return 0;
};

const versionMeetsTarget = (version, target) => compareFirmwareVersions(version, target) >= 0;

// Parse firmware filename
const parseFirmwareModel = (fileName) => {
  if (fileName.includes('NEWTON_SAAS_M9')) return { type: 'Newton Gateway', code: 'M9' };
  if (fileName.includes('NEWTON_SAAS_M10')) return { type: 'Newton USB Gateway', code: 'M10' };
  if (fileName.includes('NEWTON_SAAS_M12')) return { type: 'Newton Cisco USB Gateway', code: 'M12' };
  if (fileName.includes('SaaSGen4')) return { type: 'SaaS Gen4', code: 'Gen4' };
  return { type: 'Unknown', code: 'Unknown' };
};

// Status Badge
const StatusBadge = ({ status, small = false }) => {
  const colors = {
    success: 'green', completed: 'green', failed: 'red', error: 'red',
    offline: 'gray', cancelled: 'gray', pending: 'blue', scheduled: 'blue',
    running: 'yellow', in_progress: 'yellow'
  };
  return <span className={`gfm-status-badge ${colors[status] || 'gray'} ${small ? 'small' : ''}`}>{status}</span>;
};

const GatewayFleetManager = () => {
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data source preference (persistent via localStorage)
  const [dataSource, setDataSource] = useState(() => {
    return localStorage.getItem('gfm-data-source') || 'live';
  });
  const [cacheStatus, setCacheStatus] = useState(null); // { exists, cachedAt, companyCount }

  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [firmwares, setFirmwares] = useState([]);
  const [firmwaresByType, setFirmwaresByType] = useState({});

  // History tab
  const [upgradeHistory, setUpgradeHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filter selections
  const [selectedGatewayType, setSelectedGatewayType] = useState('all'); // Default to all
  const [selectedFirmware, setSelectedFirmware] = useState(null);
  const [targetVersion, setTargetVersion] = useState('');
  const [matchExactVersion, setMatchExactVersion] = useState(false);
  const [whitelistFilter, setWhitelistFilter] = useState('all'); // 'all', 'enabled', 'disabled'
  const [companyTab, setCompanyTab] = useState('active'); // 'active', 'expired'
  const [companySearch, setCompanySearch] = useState('');
  const [showWhitelistModal, setShowWhitelistModal] = useState(false);
  const [whitelistModalFilter, setWhitelistModalFilter] = useState('enabled'); // 'enabled', 'disabled', 'all'
  const [whitelistModalSearch, setWhitelistModalSearch] = useState('');
  const [fwDistSort, setFwDistSort] = useState({ by: 'count', dir: 'desc' }); // Firmware distribution sort

  // Table sorting
  const [sortColumn, setSortColumn] = useState('gatewayCount');
  const [sortDirection, setSortDirection] = useState('desc');

  // Firmware detail modal
  const [showFirmwareModal, setShowFirmwareModal] = useState(false);
  const [selectedFirmwareVersion, setSelectedFirmwareVersion] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedStores, setExpandedStores] = useState({}); // { "companyCode:storeId": [gateways] }
  const [loadingStores, setLoadingStores] = useState({}); // { "companyCode:storeId": true }

  // Upgrade tab
  const [upgradeTargetFirmware, setUpgradeTargetFirmware] = useState(null);
  const [upgradeSelectedCompanies, setUpgradeSelectedCompanies] = useState([]);
  const [upgradeSelectedStores, setUpgradeSelectedStores] = useState({}); // { companyCode: [storeIds] }
  const [upgradeExpandedCompanies, setUpgradeExpandedCompanies] = useState({});
  const [upgradeSettings, setUpgradeSettings] = useState({ onlyOnline: true, batchSize: 10 });
  const [upgradeShowOnlyActive, setUpgradeShowOnlyActive] = useState(false);
  const [upgradeCompanySearch, setUpgradeCompanySearch] = useState('');

  // Scheduling
  const [scheduleType, setScheduleType] = useState('immediate'); // 'immediate' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [useMaintenanceWindow, setUseMaintenanceWindow] = useState(false);
  const [maintenanceStart, setMaintenanceStart] = useState('22:00');
  const [maintenanceEnd, setMaintenanceEnd] = useState('06:00');
  const [notificationEmail, setNotificationEmail] = useState('');

  // Upgrade jobs
  const [upgradeJobs, setUpgradeJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);

  // Job scope modal (shows companies/stores for a job)
  const [showJobScopeModal, setShowJobScopeModal] = useState(false);
  const [jobScopeData, setJobScopeData] = useState(null);

  // Upgrade summary panel - tracks expanded state for Company -> Store -> Gateway tree
  const [summaryExpandedCompanies, setSummaryExpandedCompanies] = useState({});
  const [summaryExpandedStores, setSummaryExpandedStores] = useState({});

  // Company detail modal (shows gateway type breakdown when clicking company code)
  const [showCompanyDetailModal, setShowCompanyDetailModal] = useState(false);
  const [selectedCompanyDetail, setSelectedCompanyDetail] = useState(null);
  const [companyModalSort, setCompanyModalSort] = useState({ by: 'count', dir: 'desc' }); // by: 'count' | 'version', dir: 'asc' | 'desc'

  // Loading progress
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, done: false });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Check cache status on mount
  const checkCacheStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/gateway-firmware/cache/status');
      setCacheStatus(response.data);
    } catch (err) {
      console.error('Error checking cache status:', err);
    }
  }, []);

  // Save data to cache
  const saveToCache = useCallback(async (summaryData, firmwaresData) => {
    try {
      await axiosInstance.post('/gateway-firmware/cache/save', {
        summary: summaryData,
        firmwares: firmwaresData
      });
      console.log('[GFM] Data saved to cache');
      checkCacheStatus(); // Update cache status
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  }, [checkCacheStatus]);

  // Fetch from cache
  const fetchFromCache = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadingProgress({ loaded: 0, total: 0, done: false });
    try {
      const response = await axiosInstance.get('/gateway-firmware/cache/data');
      setSummary(response.data.summary);
      const fwList = response.data.firmwares || [];
      setFirmwares(fwList);

      // Build firmwaresByType
      const byType = {};
      fwList.forEach(fw => {
        const parsed = parseFirmwareModel(fw.fileName);
        if (!byType[parsed.type]) byType[parsed.type] = [];
        byType[parsed.type].push({ ...fw, parsedType: parsed });
      });
      Object.keys(byType).forEach(key => byType[key].sort((a, b) => b.version.localeCompare(a.version)));
      setFirmwaresByType(byType);

      setLoading(false);
      setLoadingProgress({ loaded: response.data.summary?.companies?.length || 0, total: response.data.summary?.companies?.length || 0, done: true });
      console.log(`[GFM] Loaded from cache (${new Date(response.data.cachedAt).toLocaleString()})`);
    } catch (err) {
      setError('No cached data available. Switch to Live Data to fetch.');
      setLoading(false);
      setLoadingProgress({ loaded: 0, total: 0, done: true });
    }
  }, []);

  // Fetch fleet summary (live data)
  const fetchSummaryLive = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadingProgress({ loaded: 0, total: 0, done: false });
    let finalSummary = null;
    try {
      const response = await axiosInstance.get('/gateway-firmware/summary');
      setSummary(response.data);
      finalSummary = response.data;
      setLoading(false);

      const companies = response.data.companies || [];
      if (companies.length > 0) {
        setLoadingProgress({ loaded: 0, total: companies.length, done: false });
        const batchSize = 25;
        let loadedCount = 0;

        for (let i = 0; i < companies.length; i += batchSize) {
          const batch = companies.slice(i, i + batchSize).map(c => c.code);
          try {
            const detailsResponse = await axiosInstance.post('/gateway-firmware/company-details', { companies: batch });
            loadedCount += batch.length;
            const isDone = loadedCount >= companies.length;
            setLoadingProgress({ loaded: loadedCount, total: companies.length, done: isDone });

            setSummary(prev => {
              if (!prev) return prev;
              const updated = { ...prev };
              const details = detailsResponse.data.companies || {};

              updated.companies = updated.companies.map(c => details[c.code] ? { ...c, ...details[c.code] } : c);

              let totalGateways = 0, totalOnline = 0, totalOffline = 0, totalStores = 0;
              const firmwareVersions = {};

              updated.companies.forEach(c => {
                if (c.gatewayCount !== null) {
                  totalGateways += c.gatewayCount || 0;
                  totalOnline += c.online || 0;
                  totalOffline += c.offline || 0;
                  totalStores += c.storeCount || 0;
                  Object.entries(c.firmwareVersions || {}).forEach(([v, count]) => {
                    firmwareVersions[v] = (firmwareVersions[v] || 0) + count;
                  });
                }
              });

              updated.totalGateways = totalGateways;
              updated.totalOnline = totalOnline;
              updated.totalOffline = totalOffline;
              updated.totalStores = totalStores;
              updated.firmwareVersions = firmwareVersions;
              finalSummary = updated;
              return updated;
            });
          } catch (batchErr) {
            loadedCount += batch.length;
            setLoadingProgress({ loaded: loadedCount, total: companies.length, done: loadedCount >= companies.length });
          }
        }
      } else {
        setLoadingProgress({ loaded: 0, total: 0, done: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setLoading(false);
      setLoadingProgress({ loaded: 0, total: 0, done: true });
    }
    return finalSummary;
  }, []);

  // Wrapper function that checks dataSource
  const fetchSummary = useCallback(async (forceLive = false) => {
    if (!forceLive && dataSource === 'local') {
      return fetchFromCache();
    }
    const summaryData = await fetchSummaryLive();
    return summaryData;
  }, [dataSource, fetchFromCache, fetchSummaryLive]);

  // Fetch firmwares
  const fetchFirmwares = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/gateway-firmware/firmwares');
      const fwList = response.data.firmwares || [];
      setFirmwares(fwList);

      const byType = {};
      fwList.forEach(fw => {
        const parsed = parseFirmwareModel(fw.fileName);
        if (!byType[parsed.type]) byType[parsed.type] = [];
        byType[parsed.type].push({ ...fw, parsedType: parsed });
      });

      Object.keys(byType).forEach(key => byType[key].sort((a, b) => b.version.localeCompare(a.version)));
      setFirmwaresByType(byType);
    } catch (err) {
      console.error('Error fetching firmwares:', err);
    }
  }, []);

  // Fetch upgrade jobs
  const fetchUpgradeJobs = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/gateway-firmware/upgrades');
      setUpgradeJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Error fetching upgrade jobs:', err);
    }
  }, []);

  // Toggle whitelist
  const toggleWhitelist = async (companyCode, enable) => {
    try {
      await axiosInstance.post(`/gateway-firmware/whitelist/${enable ? 'enable' : 'disable'}`, { company: companyCode });
      // Update local state immediately
      setSummary(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        updated.companies = updated.companies.map(c =>
          c.code === companyCode ? { ...c, whitelistEnabled: enable } : c
        );
        updated.whitelistEnabled = updated.companies.filter(c => c.whitelistEnabled).length;
        return updated;
      });
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Bulk whitelist toggle (enable/disable all filtered companies)
  const bulkWhitelist = async (companies, enable) => {
    const action = enable ? 'enable' : 'disable';
    const count = companies.length;
    if (!window.confirm(`${enable ? 'Enable' : 'Disable'} whitelist for ${count} companies?\n\nA snapshot will be saved before making changes.`)) {
      return;
    }

    try {
      const response = await axiosInstance.post('/gateway-firmware/whitelist/bulk', {
        companies: companies.map(c => c.code),
        enable
      });

      if (response.data.success) {
        // Update local state
        setSummary(prev => {
          if (!prev) return prev;
          const updated = { ...prev };
          const codes = new Set(companies.map(c => c.code));
          updated.companies = updated.companies.map(c =>
            codes.has(c.code) ? { ...c, whitelistEnabled: enable } : c
          );
          updated.whitelistEnabled = updated.companies.filter(c => c.whitelistEnabled).length;
          return updated;
        });
        alert(`${response.data.successCount} of ${count} companies updated.\nSnapshot saved: ${response.data.snapshotId}`);
      }
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Start upgrade
  const startUpgrade = async () => {
    if (!upgradeTargetFirmware) {
      alert('Please select target firmware');
      return;
    }

    // Build scope - either full companies or specific stores
    const scope = [];
    upgradeSelectedCompanies.forEach(code => {
      const stores = upgradeSelectedStores[code];
      if (stores && stores.length > 0) {
        scope.push({ company: code, stores });
      } else {
        scope.push({ company: code, stores: 'all' });
      }
    });

    if (scope.length === 0) {
      alert('Please select at least one company or store');
      return;
    }

    // Build scheduling config
    const scheduling = {
      type: scheduleType === 'immediate' ? 'immediate' : (useMaintenanceWindow ? 'maintenance_window' : 'scheduled')
    };

    if (scheduleType === 'scheduled') {
      scheduling.scheduledTime = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      scheduling.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (useMaintenanceWindow) {
        scheduling.maintenanceWindowStart = maintenanceStart;
        scheduling.maintenanceWindowEnd = maintenanceEnd;
      }
    }

    try {
      const response = await axiosInstance.post('/gateway-firmware/upgrade', {
        name: `Upgrade to ${upgradeTargetFirmware.version}`,
        targetFirmware: {
          version: upgradeTargetFirmware.version,
          fileName: upgradeTargetFirmware.fileName,
          gatewayModel: upgradeTargetFirmware.gatewayModel
        },
        scope,
        filters: { onlyOnline: upgradeShowOnlyActive },
        settings: { ...upgradeSettings, onlyOnline: upgradeShowOnlyActive },
        scheduling,
        notificationEmail: notificationEmail || undefined,
        startImmediately: scheduleType === 'immediate'
      });

      if (response.data.success) {
        const msg = scheduleType === 'immediate'
          ? `Upgrade job started: ${response.data.jobId}`
          : `Upgrade scheduled for ${scheduledDate} ${scheduledTime}: ${response.data.jobId}`;
        alert(msg);
        setScheduleType('immediate');
        setScheduledDate('');
        setScheduledTime('');
        setNotificationEmail('');
        setActiveTab('jobs');
        fetchUpgradeJobs();
      }
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Cancel job
  const cancelJob = async (jobId) => {
    if (!window.confirm('Cancel this upgrade job?')) return;
    try {
      await axiosInstance.post(`/gateway-firmware/upgrade/${jobId}/cancel`);
      fetchUpgradeJobs();
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Open job scope modal to show companies/stores
  const openJobScopeModal = (job) => {
    // Extract companies and stores from job scope
    const entries = job.scope?.entries || [];
    const companies = job.scope?.companies || [];

    // Build scope data for modal
    const scopeData = {
      jobId: job.jobId,
      name: job.name,
      entries: entries.length > 0 ? entries : companies.map(c => ({ company: c, stores: 'all' }))
    };

    setJobScopeData(scopeData);
    setShowJobScopeModal(true);
  };

  // Fetch upgrade history (completed/cancelled/failed jobs)
  const fetchUpgradeHistory = useCallback(async (page = 1) => {
    setLoadingHistory(true);
    try {
      const response = await axiosInstance.get('/gateway-firmware/upgrades/history', {
        params: { page, limit: 20 }
      });
      setUpgradeHistory(response.data.jobs || []);
      setHistoryPagination(response.data.pagination || { page: 1, total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Error fetching upgrade history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Handle data source change
  const handleDataSourceChange = (newSource) => {
    setDataSource(newSource);
    localStorage.setItem('gfm-data-source', newSource);
  };

  // Initial load
  useEffect(() => {
    checkCacheStatus();
    fetchSummary();
    fetchFirmwares();
    fetchUpgradeJobs();
  }, [checkCacheStatus, fetchSummary, fetchFirmwares, fetchUpgradeJobs]);

  // Save to cache when live data finishes loading
  useEffect(() => {
    if (loadingProgress.done && summary && firmwares.length > 0) {
      saveToCache(summary, firmwares);
    }
  }, [dataSource, loadingProgress.done, summary, firmwares, saveToCache]);

  // Export data to Excel
  const exportToExcel = () => {
    if (!summary?.companies) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1: Company summary
    const companyRows = summary.companies
      .filter(c => c.gatewayCount !== null)
      .map(c => ({
        'Company Code': c.code,
        'Company Name': c.name || '',
        'Stores': c.storeCount || 0,
        'Total Gateways': c.gatewayCount || 0,
        'Online': c.online || 0,
        'Offline': c.offline || 0,
        'Whitelist': c.whitelistEnabled ? 'Enabled' : c.whitelistEnabled === false ? 'Disabled' : 'N/A',
      }));
    const wsCompanies = XLSX.utils.json_to_sheet(companyRows);
    XLSX.utils.book_append_sheet(wb, wsCompanies, 'Companies');

    // Sheet 2: Firmware distribution (one row per company + version)
    const fwRows = [];
    summary.companies.forEach(c => {
      if (!c.firmwareVersions) return;
      Object.entries(c.firmwareVersions).forEach(([version, count]) => {
        const parsed = parseFirmwareVersion(version);
        fwRows.push({
          'Company Code': c.code,
          'Company Name': c.name || '',
          'Gateway Type': parsed.type,
          'Firmware Version': version,
          'Count': count,
        });
      });
    });
    const wsFirmware = XLSX.utils.json_to_sheet(fwRows);
    XLSX.utils.book_append_sheet(wb, wsFirmware, 'Firmware by Company');

    // Sheet 3: Firmware totals
    const totalRows = Object.entries(summary.firmwareVersions || {})
      .sort((a, b) => b[1] - a[1])
      .map(([version, count]) => {
        const parsed = parseFirmwareVersion(version);
        return {
          'Gateway Type': parsed.type,
          'Firmware Version': version,
          'Count': count,
          'Percentage': `${((count / (summary.totalGateways || 1)) * 100).toFixed(2)}%`,
        };
      });
    const wsTotals = XLSX.utils.json_to_sheet(totalRows);
    XLSX.utils.book_append_sheet(wb, wsTotals, 'Firmware Totals');

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Gateway-Firmware-${date}.xlsx`);
  };

  // Get firmware versions filtered by gateway type
  const getFilteredFirmwareVersions = () => {
    if (!summary?.firmwareVersions) return {};
    if (selectedGatewayType === 'all') return summary.firmwareVersions;

    // Filter versions that match the selected gateway type
    const filtered = {};
    Object.entries(summary.firmwareVersions).forEach(([version, count]) => {
      const parsed = parseFirmwareVersion(version);
      if (parsed.type === selectedGatewayType) {
        filtered[version] = count;
      }
    });
    return filtered;
  };

  // Calculate stats (filtered by gateway type)
  const getStats = () => {
    if (!summary) return { totalGateways: 0, onTarget: 0, needsUpgrade: 0, percentOnTarget: 0, online: 0, offline: 0 };

    const filteredVersions = getFilteredFirmwareVersions();
    let total = Object.values(filteredVersions).reduce((sum, count) => sum + count, 0);
    let onTarget = 0;

    // Calculate online/offline for filtered gateway type
    let online = 0, offline = 0;
    if (selectedGatewayType === 'all') {
      online = summary.totalOnline || 0;
      offline = summary.totalOffline || 0;
    } else {
      // Need to calculate from company data
      (summary.companies || []).forEach(c => {
        if (!c.firmwareVersions) return;
        Object.entries(c.firmwareVersions).forEach(([version, count]) => {
          const parsed = parseFirmwareVersion(version);
          if (parsed.type === selectedGatewayType) {
            // Approximate online ratio based on company's overall ratio
            const ratio = c.gatewayCount > 0 ? (c.online || 0) / c.gatewayCount : 0;
            online += Math.round(count * ratio);
            offline += count - Math.round(count * ratio);
          }
        });
      });
    }

    if (targetVersion) {
      Object.entries(filteredVersions).forEach(([version, count]) => {
        if (matchExactVersion) {
          if (version.replace(/^N/, '') === targetVersion.replace(/^N/, '')) onTarget += count;
        } else {
          if (versionMeetsTarget(version, targetVersion)) onTarget += count;
        }
      });
    }

    return {
      totalGateways: total,
      onTarget,
      needsUpgrade: total - onTarget,
      percentOnTarget: total > 0 ? Math.round((onTarget / total) * 100) : 0,
      online,
      offline
    };
  };

  // Get companies with specific firmware version
  const getCompaniesWithVersion = (version) => {
    if (!summary) return [];
    return (summary.companies || []).filter(c =>
      c.firmwareVersions && c.firmwareVersions[version] > 0
    ).map(c => ({
      ...c,
      versionCount: c.firmwareVersions[version]
    })).sort((a, b) => b.versionCount - a.versionCount);
  };

  // Handle firmware box click
  const handleFirmwareClick = (version) => {
    setSelectedFirmwareVersion(version);
    setExpandedCompanies({});
    setExpandedStores({});
    setLoadingStores({});
    setShowFirmwareModal(true);
  };

  // Fetch gateway details for a store
  const fetchStoreGateways = async (company, store, version) => {
    const key = `${company}:${store}`;

    // Toggle off if already expanded
    if (expandedStores[key]) {
      setExpandedStores(prev => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }

    setLoadingStores(prev => ({ ...prev, [key]: true }));
    try {
      const response = await axiosInstance.get(`/gateway-firmware/store-gateways/${company}/${store}`, {
        params: { version }
      });
      setExpandedStores(prev => ({ ...prev, [key]: response.data.gateways || [] }));
    } catch (err) {
      console.error('Error fetching store gateways:', err);
      setExpandedStores(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingStores(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  // Sort companies
  const sortCompanies = (companies) => {
    return [...companies].sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'code': aVal = a.code; bVal = b.code; break;
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'storeCount': aVal = a.storeCount || 0; bVal = b.storeCount || 0; break;
        case 'gatewayCount': aVal = a.gatewayCount || 0; bVal = b.gatewayCount || 0; break;
        case 'online': aVal = a.online || 0; bVal = b.online || 0; break;
        case 'whitelist': aVal = a.whitelistEnabled === true ? 2 : (a.whitelistEnabled === false ? 1 : 0); bVal = b.whitelistEnabled === true ? 2 : (b.whitelistEnabled === false ? 1 : 0); break;
        default: aVal = a.gatewayCount || 0; bVal = b.gatewayCount || 0;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const stats = getStats();
  const whitelistEnabled = summary?.whitelistEnabled || 0;
  const whitelistUnknown = summary?.whitelistUnknown || 0;
  const totalCompanies = summary?.totalCompanies || 0;
  const accessibleCompanies = totalCompanies - whitelistUnknown;

  // Render sort indicator
  const SortIndicator = ({ column }) => (
    <span className="gfm-sort-indicator">
      {sortColumn === column ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
    </span>
  );

  // Render Dashboard
  const renderDashboard = () => {
    // Separate active vs expired companies
    // Note: Don't filter by c.error - expired companies get error when company-details fails
    const allCompanies = summary?.companies || [];
    const activeCompanies = allCompanies.filter(c => c.whitelistEnabled !== null);
    const expiredCompanies = allCompanies.filter(c => c.whitelistEnabled === null);

    // Apply tab filter first
    let filteredCompanies = companyTab === 'expired' ? expiredCompanies : activeCompanies;

    // Apply whitelist filter (only for active tab)
    if (companyTab === 'active') {
      if (whitelistFilter === 'enabled') filteredCompanies = filteredCompanies.filter(c => c.whitelistEnabled === true);
      if (whitelistFilter === 'disabled') filteredCompanies = filteredCompanies.filter(c => c.whitelistEnabled === false);
    }

    // Apply text search filter
    if (companySearch.trim()) {
      const search = companySearch.toLowerCase().trim();
      filteredCompanies = filteredCompanies.filter(c =>
        c.code?.toLowerCase().includes(search) ||
        c.name?.toLowerCase().includes(search)
      );
    }

    filteredCompanies = sortCompanies(filteredCompanies);
    const totalPages = Math.ceil(filteredCompanies.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedCompanies = filteredCompanies.slice(startIndex, startIndex + pageSize);

    return (
      <>
        {/* Gateway Type & Firmware Selection - Compact Toolbar */}
        <div className="gfm-upgrade-toolbar">
          <span className="gfm-toolbar-label">Gateway Type:</span>
          <select className="gfm-toolbar-select" value={selectedGatewayType} onChange={(e) => {
            setSelectedGatewayType(e.target.value);
            setSelectedFirmware(null);
            setTargetVersion('');
          }}>
            <option value="all">All Gateway Types</option>
            {Object.keys(firmwaresByType).sort((a, b) => {
              // Custom sort: Newton Gateway first, Newton USB Gateway second, Unknown last
              const order = { 'Newton Gateway': 1, 'Newton USB Gateway': 2, 'Unknown': 999 };
              return (order[a] || 50) - (order[b] || 50);
            }).map(type => <option key={type} value={type}>{type}</option>)}
          </select>

          {selectedGatewayType && selectedGatewayType !== 'all' && firmwaresByType[selectedGatewayType] && (
            <>
              <span className="gfm-toolbar-label">Target Firmware:</span>
              <select className="gfm-toolbar-select" style={{ minWidth: '180px' }} value={selectedFirmware?.fileName || ''} onChange={(e) => {
                const fw = firmwaresByType[selectedGatewayType].find(f => f.fileName === e.target.value);
                setSelectedFirmware(fw);
                setTargetVersion(fw?.version || '');
              }}>
                <option value="">Select Version...</option>
                {firmwaresByType[selectedGatewayType].map(fw => (
                  <option key={fw.fileName} value={fw.fileName}>v{fw.version}</option>
                ))}
              </select>
            </>
          )}

          {targetVersion && (
            <>
              <span className="gfm-toolbar-sep">|</span>
              <label className="gfm-toolbar-checkbox">
                <input type="checkbox" checked={matchExactVersion} onChange={(e) => setMatchExactVersion(e.target.checked)} />
                Exact only
              </label>
              <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                {matchExactVersion ? '(exact matches)' : `(v${targetVersion}+)`}
              </span>
            </>
          )}
        </div>

        {/* Summary Cards - Compact Aero Style */}
        <div className="gfm-summary-cards gfm-cards-compact">
          <div className="gfm-card-mini blue">
            <div className="gfm-card-mini-icon">📡</div>
            <div className="gfm-card-mini-content">
              <div className="gfm-card-mini-value">{stats.totalGateways > 0 ? stats.totalGateways.toLocaleString() : (summary?.companies?.some(c => c.gatewayCount === null) ? '...' : '0')}</div>
              <div className="gfm-card-mini-label">{selectedGatewayType === 'all' ? 'Total Gateways' : selectedGatewayType}</div>
              <div className="gfm-card-mini-sub">{stats.online} online / {stats.offline} offline</div>
            </div>
          </div>

          <div className={`gfm-card-mini ${stats.percentOnTarget >= 90 ? 'green' : stats.percentOnTarget >= 50 ? 'yellow' : 'red'}`}>
            <div className="gfm-card-mini-icon">✅</div>
            <div className="gfm-card-mini-content">
              <div className="gfm-card-mini-value">{targetVersion ? `${stats.percentOnTarget}%` : '--'}</div>
              <div className="gfm-card-mini-label">On Target</div>
              <div className="gfm-card-mini-sub">{targetVersion ? `${stats.onTarget.toLocaleString()} of ${stats.totalGateways.toLocaleString()}` : 'Select firmware'}</div>
            </div>
          </div>

          <div className="gfm-card-mini orange">
            <div className="gfm-card-mini-icon">⬆️</div>
            <div className="gfm-card-mini-content">
              <div className="gfm-card-mini-value">{targetVersion ? stats.needsUpgrade.toLocaleString() : '--'}</div>
              <div className="gfm-card-mini-label">Needs Upgrade</div>
              <div className="gfm-card-mini-sub">{targetVersion ? 'Below target' : 'Select firmware'}</div>
            </div>
          </div>

          <div className="gfm-card-mini purple clickable" onClick={() => setShowWhitelistModal(true)}>
            <div className="gfm-card-mini-icon">🔒</div>
            <div className="gfm-card-mini-content">
              <div className="gfm-card-mini-value">{whitelistEnabled}/{accessibleCompanies}</div>
              <div className="gfm-card-mini-label">Whitelist</div>
              <div className="gfm-card-mini-sub">{accessibleCompanies > 0 ? `${Math.round((whitelistEnabled / accessibleCompanies) * 100)}%` : '0%'}</div>
            </div>
          </div>
        </div>

        {/* Firmware Distribution - clickable, grouped by type when "all" is selected */}
        {summary?.firmwareVersions && Object.keys(summary.firmwareVersions).length > 0 && (() => {
          const filteredVersions = getFilteredFirmwareVersions();
          const totalFiltered = Object.values(filteredVersions).reduce((s, c) => s + c, 0);

          // Sort function for versions
          const sortFwVersions = (versions) => {
            if (fwDistSort.by === 'version') {
              versions.sort((a, b) => {
                const cmp = compareFirmwareVersions(a.version, b.version);
                return fwDistSort.dir === 'asc' ? cmp : -cmp;
              });
            } else {
              versions.sort((a, b) => fwDistSort.dir === 'asc' ? a.count - b.count : b.count - a.count);
            }
            return versions;
          };

          // Toggle sort handler
          const toggleFwSort = (sortBy) => {
            setFwDistSort(prev => {
              if (prev.by === sortBy) {
                return { by: sortBy, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
              }
              return { by: sortBy, dir: 'desc' };
            });
          };

          // Group by gateway type when "all" is selected
          if (selectedGatewayType === 'all') {
            const groupedByType = {};
            Object.entries(filteredVersions).forEach(([version, count]) => {
              const parsed = parseFirmwareVersion(version);
              const type = parsed.type || 'Unknown';
              if (!groupedByType[type]) groupedByType[type] = [];
              groupedByType[type].push({ version, count, parsed });
            });

            // Custom sort: Newton Gateway first, Newton USB Gateway second, Unknown last
            const typeOrder = { 'Newton Gateway': 1, 'Newton USB Gateway': 2, 'Unknown': 999 };
            const sortedTypes = Object.entries(groupedByType)
              .map(([type, versions]) => ({
                type,
                versions: sortFwVersions([...versions]),
                total: versions.reduce((s, v) => s + v.count, 0)
              }))
              .sort((a, b) => (typeOrder[a.type] || 50) - (typeOrder[b.type] || 50));

            return (
              <div className="gfm-section">
                <div className="gfm-section-header-row">
                  <h3>Firmware Distribution by Gateway Type ({Object.keys(filteredVersions).length} versions)</h3>
                  <div className="gfm-sort-controls">
                    <span className="gfm-sort-label">Sort:</span>
                    <button
                      className={`gfm-sort-btn ${fwDistSort.by === 'count' ? 'active' : ''}`}
                      onClick={() => toggleFwSort('count')}
                    >
                      Count {fwDistSort.by === 'count' && (fwDistSort.dir === 'desc' ? '↓' : '↑')}
                    </button>
                    <button
                      className={`gfm-sort-btn ${fwDistSort.by === 'version' ? 'active' : ''}`}
                      onClick={() => toggleFwSort('version')}
                    >
                      Version {fwDistSort.by === 'version' && (fwDistSort.dir === 'desc' ? '↓' : '↑')}
                    </button>
                  </div>
                </div>
                <div className="gfm-fw-columns">
                  {sortedTypes.map(({ type, versions, total }) => (
                    <div key={type} className="gfm-fw-column">
                      <div className="gfm-fw-column-header">
                        <span className="gfm-fw-type-name">{type}</span>
                        <span className="gfm-fw-type-total">{total.toLocaleString()}</span>
                      </div>
                      <div className="gfm-fw-column-body">
                        {versions.map(({ version, count }) => {
                          const percent = ((count / total) * 100).toFixed(2);
                          const isTarget = targetVersion && version.includes(targetVersion.replace(/^N/, ''));
                          return (
                            <div key={version} className={`gfm-fw-item clickable ${isTarget ? 'target' : ''}`} onClick={() => handleFirmwareClick(version)}>
                              <span className="gfm-fw-version">{version}</span>
                              <span className={`gfm-fw-pill ${isTarget ? 'green' : ''}`}>{count.toLocaleString()} ({percent}%)</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Single type selected - show same column style as "all"
          let sortedVersions = Object.entries(filteredVersions)
            .filter(([, count]) => count > 0)
            .map(([version, count]) => ({ version, count }));

          // Apply sorting
          if (fwDistSort.by === 'version') {
            sortedVersions.sort((a, b) => {
              const cmp = compareFirmwareVersions(a.version, b.version);
              return fwDistSort.dir === 'asc' ? cmp : -cmp;
            });
          } else {
            sortedVersions.sort((a, b) => fwDistSort.dir === 'asc' ? a.count - b.count : b.count - a.count);
          }

          return (
            <div className="gfm-section">
              <div className="gfm-section-header-row">
                <h3>Firmware Distribution - {selectedGatewayType}</h3>
                <div className="gfm-sort-controls">
                  <span className="gfm-sort-label">Sort:</span>
                  <button
                    className={`gfm-sort-btn ${fwDistSort.by === 'count' ? 'active' : ''}`}
                    onClick={() => toggleFwSort('count')}
                  >
                    Count {fwDistSort.by === 'count' && (fwDistSort.dir === 'desc' ? '↓' : '↑')}
                  </button>
                  <button
                    className={`gfm-sort-btn ${fwDistSort.by === 'version' ? 'active' : ''}`}
                    onClick={() => toggleFwSort('version')}
                  >
                    Version {fwDistSort.by === 'version' && (fwDistSort.dir === 'desc' ? '↓' : '↑')}
                  </button>
                </div>
              </div>
              <div className="gfm-fw-columns">
                <div className="gfm-fw-column">
                  <div className="gfm-fw-column-header">
                    <span className="gfm-fw-type-name">{selectedGatewayType}</span>
                    <span className="gfm-fw-type-total">{totalFiltered.toLocaleString()}</span>
                  </div>
                  <div className="gfm-fw-column-body">
                    {sortedVersions.map(({ version, count }) => {
                      const percent = ((count / totalFiltered) * 100).toFixed(2);
                      const isTarget = targetVersion && version.includes(targetVersion.replace(/^N/, ''));
                      return (
                        <div key={version} className={`gfm-fw-item clickable ${isTarget ? 'target' : ''}`} onClick={() => handleFirmwareClick(version)}>
                          <span className="gfm-fw-version">{version}</span>
                          <span className={`gfm-fw-pill ${isTarget ? 'green' : ''}`}>{count.toLocaleString()} ({percent}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Company Table with tabs and filters */}
        <div className="gfm-section">
          <div className="gfm-section-header">
            <h3>Company Breakdown</h3>
            <div className="gfm-table-tabs">
              <span className="gfm-tabs-label">Companies:</span>
              <button
                className={`gfm-table-tab ${companyTab === 'active' ? 'active' : ''}`}
                onClick={() => { setCompanyTab('active'); setCurrentPage(1); }}
              >
                Active ({activeCompanies.length})
              </button>
              <button
                className={`gfm-table-tab ${companyTab === 'expired' ? 'active' : ''}`}
                onClick={() => { setCompanyTab('expired'); setCurrentPage(1); }}
              >
                Expired ({expiredCompanies.length})
              </button>
            </div>
            <div className="gfm-table-filters">
              <input
                type="text"
                className="gfm-search-input"
                placeholder="Search company..."
                value={companySearch}
                onChange={(e) => { setCompanySearch(e.target.value); setCurrentPage(1); }}
              />
              {companyTab === 'active' && (
                <select value={whitelistFilter} onChange={(e) => { setWhitelistFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Active</option>
                  <option value="enabled">Whitelist Enabled</option>
                  <option value="disabled">Whitelist Disabled</option>
                </select>
              )}
            </div>
          </div>

          <div className="gfm-company-selector">
            <table className="gfm-company-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('code')} className="sortable">Company<SortIndicator column="code" /></th>
                  {companyTab === 'active' && (
                    <>
                      <th onClick={() => handleSort('storeCount')} className="sortable">Stores<SortIndicator column="storeCount" /></th>
                      <th onClick={() => handleSort('gatewayCount')} className="sortable">Gateways<SortIndicator column="gatewayCount" /></th>
                      <th onClick={() => handleSort('online')} className="sortable">Online<SortIndicator column="online" /></th>
                      <th>On Target</th>
                      <th onClick={() => handleSort('whitelist')} className="sortable">Whitelist<SortIndicator column="whitelist" /></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.map(company => {
                  if (companyTab === 'expired') {
                    return (
                      <tr key={company.code}>
                        <td><span className="company-code clickable" onClick={() => { setSelectedCompanyDetail(company); setShowCompanyDetailModal(true); }}>{company.code}</span><span className="company-name">{company.name}</span></td>
                      </tr>
                    );
                  }

                  const isLoading = company.gatewayCount === null;
                  let onTarget = 0;
                  if (targetVersion && !isLoading) {
                    Object.entries(company.firmwareVersions || {}).forEach(([version, count]) => {
                      if (matchExactVersion) {
                        if (version.replace(/^N/, '') === targetVersion.replace(/^N/, '')) onTarget += count;
                      } else {
                        if (versionMeetsTarget(version, targetVersion)) onTarget += count;
                      }
                    });
                  }
                  const percentOnTarget = company.gatewayCount > 0 ? Math.round((onTarget / company.gatewayCount) * 100) : 100;

                  return (
                    <tr key={company.code}>
                      <td><span className="company-code clickable" onClick={() => { setSelectedCompanyDetail(company); setShowCompanyDetailModal(true); }}>{company.code}</span><span className="company-name">{company.name}</span></td>
                      <td className="stats-cell">{isLoading ? <span className="text-gray">...</span> : company.storeCount}</td>
                      <td className="stats-cell">{isLoading ? <span className="text-gray">...</span> : (company.gatewayCount || 0)}</td>
                      <td className="stats-cell">{isLoading ? <span className="text-gray">...</span> : <span className={company.online > 0 ? 'online-count' : 'text-red'}>{company.online || 0}/{company.gatewayCount || 0}</span>}</td>
                      <td className="stats-cell">
                        {isLoading ? <span className="text-gray">...</span> : targetVersion ? (
                          <div className="gfm-mini-progress">
                            <div className={`gfm-mini-fill ${percentOnTarget >= 100 ? 'green' : percentOnTarget >= 50 ? 'yellow' : 'red'}`} style={{ width: `${percentOnTarget}%` }} />
                            <span>{percentOnTarget}%</span>
                          </div>
                        ) : <span className="text-gray">--</span>}
                      </td>
                      <td className="stats-cell">
                        <button className={`gfm-whitelist-btn ${company.whitelistEnabled ? 'enabled' : 'disabled'}`} onClick={() => toggleWhitelist(company.code, !company.whitelistEnabled)}>
                          {company.whitelistEnabled ? '✓ Enabled' : '✗ Disabled'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="gfm-pagination">
              <button className="gfm-page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
              <button className="gfm-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
              <span className="gfm-page-info">Page {currentPage} of {totalPages} ({filteredCompanies.length} companies)</span>
              <button className="gfm-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
              <button className="gfm-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
            </div>
          )}
        </div>
      </>
    );
  };

  // Render Upgrade Tab
  const renderUpgrade = () => {
    // Helper to count gateways of selected type for a company
    const getTypeGatewayCount = (company) => {
      if (!company.firmwareVersions) return { total: 0, online: 0 };
      if (selectedGatewayType === 'all') {
        return { total: company.gatewayCount || 0, online: company.online || 0 };
      }
      let total = 0;
      Object.entries(company.firmwareVersions).forEach(([version, count]) => {
        const parsed = parseFirmwareVersion(version);
        if (parsed.type === selectedGatewayType) {
          total += count;
        }
      });
      // Estimate online based on ratio
      const ratio = company.gatewayCount > 0 ? (company.online || 0) / company.gatewayCount : 0;
      return { total, online: Math.round(total * ratio) };
    };

    // Filter companies based on selected gateway type
    let companiesWithGateways = (summary?.companies || []).filter(c => {
      const counts = getTypeGatewayCount(c);
      return counts.total > 0;
    });

    if (upgradeShowOnlyActive) {
      companiesWithGateways = companiesWithGateways.filter(c => {
        const counts = getTypeGatewayCount(c);
        return counts.online > 0;
      });
    }

    // Filter by search term
    if (upgradeCompanySearch.trim()) {
      const searchLower = upgradeCompanySearch.toLowerCase().trim();
      companiesWithGateways = companiesWithGateways.filter(c =>
        c.code?.toLowerCase().includes(searchLower) ||
        c.name?.toLowerCase().includes(searchLower)
      );
    }

    // Sort alphabetically by company code
    companiesWithGateways = companiesWithGateways.sort((a, b) => {
      return (a.code || '').localeCompare(b.code || '');
    });

    return (
      <>
        {/* Top bar: Gateway Type + Firmware + Actions - all inline */}
        <div className="gfm-upgrade-toolbar">
          <span className="gfm-toolbar-label">Gateway Type:</span>
          <select className="gfm-toolbar-select" value={selectedGatewayType} onChange={(e) => {
            setSelectedGatewayType(e.target.value);
            setUpgradeTargetFirmware(null);
          }}>
            <option value="all">All Gateway Types</option>
            {Object.keys(firmwaresByType).sort((a, b) => {
              // Custom sort: Newton Gateway first, Newton USB Gateway second, Unknown last
              const order = { 'Newton Gateway': 1, 'Newton USB Gateway': 2, 'Unknown': 999 };
              return (order[a] || 50) - (order[b] || 50);
            }).map(type => <option key={type} value={type}>{type}</option>)}
          </select>

          {selectedGatewayType && selectedGatewayType !== 'all' && firmwaresByType[selectedGatewayType] && (
            <>
              <span className="gfm-toolbar-label">Firmware:</span>
              <select className="gfm-toolbar-select" value={upgradeTargetFirmware?.fileName || ''} onChange={(e) => {
                const fw = firmwaresByType[selectedGatewayType].find(f => f.fileName === e.target.value);
                setUpgradeTargetFirmware(fw);
              }}>
                <option value="">Select...</option>
                {firmwaresByType[selectedGatewayType].map(fw => (
                  <option key={fw.fileName} value={fw.fileName}>v{fw.version}</option>
                ))}
              </select>
            </>
          )}

          <div className="gfm-toolbar-spacer" />

          <label className="gfm-toolbar-checkbox">
            <input type="checkbox" checked={upgradeShowOnlyActive} onChange={(e) => setUpgradeShowOnlyActive(e.target.checked)} />
            Only online
          </label>
          <button className="gfm-toolbar-btn" onClick={() => setUpgradeSelectedCompanies(companiesWithGateways.map(c => c.code))}>
            All ({companiesWithGateways.length})
          </button>
          <button className="gfm-toolbar-btn" onClick={() => { setUpgradeSelectedCompanies([]); setUpgradeSelectedStores({}); }}>Clear</button>
          <span className="gfm-toolbar-count">{upgradeSelectedCompanies.length} sel</span>
        </div>

        {/* Search Filter */}
        <div className="gfm-upgrade-search">
          <input
            type="text"
            className="gfm-search-input"
            placeholder="Search companies by code or name..."
            value={upgradeCompanySearch}
            onChange={(e) => setUpgradeCompanySearch(e.target.value)}
          />
          {upgradeCompanySearch && (
            <button className="gfm-search-clear" onClick={() => setUpgradeCompanySearch('')}>×</button>
          )}
        </div>

        {/* Two-column layout: Company selection (left) + Upgrade Summary (right) */}
        <div className="gfm-upgrade-columns" style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
          {/* Company Table - left side */}
          <div className="gfm-company-selector" style={{ flex: upgradeSelectedCompanies.length > 0 ? '0 0 55%' : 1, minHeight: 0, maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
            <table className="gfm-company-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}></th>
                <th>Company</th>
                <th style={{ width: '60px', textAlign: 'center' }}>Stores</th>
                <th style={{ width: '70px', textAlign: 'center' }}>GWs</th>
                <th style={{ width: '60px', textAlign: 'center' }}>Online</th>
                <th style={{ width: '30px' }}></th>
              </tr>
            </thead>
            <tbody>
              {companiesWithGateways.map(company => {
                const isSelected = upgradeSelectedCompanies.includes(company.code);
                const isExpanded = upgradeExpandedCompanies[company.code];
                const selectedStores = upgradeSelectedStores[company.code] || [];
                const typeCounts = getTypeGatewayCount(company);

                return (
                  <React.Fragment key={company.code}>
                    <tr className={isSelected ? 'selected' : ''}>
                      <td><input type="checkbox" checked={isSelected} onChange={(e) => {
                        if (e.target.checked) setUpgradeSelectedCompanies(prev => [...prev, company.code]);
                        else { setUpgradeSelectedCompanies(prev => prev.filter(c => c !== company.code)); setUpgradeSelectedStores(prev => { const next = { ...prev }; delete next[company.code]; return next; }); }
                      }} /></td>
                      <td><span className="company-code">{company.code}</span><span className="company-name">{company.name}</span></td>
                      <td className="stats-cell">{company.storeCount || 0}</td>
                      <td className="stats-cell">{typeCounts.total || 0}</td>
                      <td className="stats-cell online-count">{typeCounts.online || 0}</td>
                      <td><button className="gfm-expand-btn" onClick={() => setUpgradeExpandedCompanies(prev => ({ ...prev, [company.code]: !prev[company.code] }))}>{isExpanded ? '▼' : '▶'}</button></td>
                    </tr>
                    {isExpanded && company.storeDetails && (
                      <tr><td colSpan="6" style={{ padding: '0', background: '#fafbfc' }}>
                        <div className="gfm-stores-list">{company.storeDetails.map(store => {
                          // Only auto-select stores that have gateways
                          const storeHasGateways = upgradeShowOnlyActive ? (store.online || 0) > 0 : (store.gatewayCount || 0) > 0;
                          // If company is selected and no specific stores chosen, only stores WITH gateways are implicitly selected
                          const allStoresSelected = isSelected && selectedStores.length === 0;
                          const isStoreChecked = (allStoresSelected && storeHasGateways) || selectedStores.includes(store.store);
                          // Reuse shared expandedStores/loadingStores from dashboard
                          const storeKey = `${company.code}:${store.store}`;
                          const storeGateways = expandedStores[storeKey] || [];
                          const isStoreExpanded = !!storeGateways.length || loadingStores[storeKey];
                          const isLoadingStore = loadingStores[storeKey];

                          return (
                            <div key={store.store} className={`gfm-store-item-container ${!storeHasGateways ? 'no-gateways' : ''}`}>
                              <div className="gfm-store-item-row">
                                <input type="checkbox" checked={isStoreChecked} onChange={(e) => {
                                  if (allStoresSelected && !e.target.checked) {
                                    const allOtherStores = company.storeDetails
                                      .filter(s => (s.gatewayCount || 0) > 0 && s.store !== store.store)
                                      .map(s => s.store);
                                    setUpgradeSelectedStores(prev => ({ ...prev, [company.code]: allOtherStores }));
                                  } else {
                                    setUpgradeSelectedStores(prev => {
                                      const current = prev[company.code] || [];
                                      return e.target.checked
                                        ? { ...prev, [company.code]: [...current, store.store] }
                                        : { ...prev, [company.code]: current.filter(s => s !== store.store) };
                                    });
                                  }
                                  if (!isSelected) setUpgradeSelectedCompanies(prev => [...prev, company.code]);
                                }} />
                                <span className="gfm-store-name">{store.store} - {store.storeName} ({store.gatewayCount})</span>
                                {storeHasGateways && (
                                  <button
                                    className="gfm-store-expand-btn"
                                    onClick={(e) => { e.stopPropagation(); fetchStoreGateways(company.code, store.store); }}
                                    title="View gateways"
                                  >
                                    {isLoadingStore ? '⏳' : (storeGateways.length > 0 ? '▼' : '▶')}
                                  </button>
                                )}
                              </div>
                              {storeGateways.length > 0 && (
                                <div className="gfm-store-gateways">
                                  {storeGateways.map(gw => (
                                    <div key={gw.mac} className={`gfm-gateway-item ${gw.status === 'CONNECTED' ? 'online' : 'offline'}`}>
                                      <span className={`gfm-gw-status ${gw.status === 'CONNECTED' ? 'online' : 'offline'}`}>
                                        {gw.status === 'CONNECTED' ? '●' : '○'}
                                      </span>
                                      <span className="gfm-gw-mac">{gw.mac}</span>
                                      <span className={`gfm-gw-firmware ${upgradeTargetFirmware && gw.version === upgradeTargetFirmware.version ? 'current' : upgradeTargetFirmware ? 'needs-upgrade' : ''}`}>
                                        v{gw.version}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}</div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Upgrade Summary Panel - right side, only when selections exist */}
          {upgradeSelectedCompanies.length > 0 && (
            <div className="gfm-upgrade-summary" style={{ flex: '0 0 44%', minHeight: 0, maxHeight: 'calc(100vh - 340px)', overflowY: 'auto', background: '#1e293b', borderRadius: '8px', padding: '12px', color: '#fff' }}>
              <div className="gfm-summary-header" style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' }}>
                <span style={{ color: '#fff' }}>Upgrade Summary</span>
                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>
                  {upgradeSelectedCompanies.length} companies
                </span>
              </div>
              <div className="gfm-summary-tree">
                {upgradeSelectedCompanies.map(companyCode => {
                  const company = (summary?.companies || []).find(c => c.code === companyCode);
                  if (!company) return null;
                  const isCompanyExpanded = summaryExpandedCompanies[companyCode];
                  const selectedStoreIds = upgradeSelectedStores[companyCode] || [];
                  // Get stores to show: either specifically selected or all with gateways
                  const storesToShow = selectedStoreIds.length > 0
                    ? (company.storeDetails || []).filter(s => selectedStoreIds.includes(s.store))
                    : (company.storeDetails || []).filter(s => upgradeShowOnlyActive ? (s.online || 0) > 0 : (s.gatewayCount || 0) > 0);
                  const totalGateways = storesToShow.reduce((sum, s) => sum + (upgradeShowOnlyActive ? (s.online || 0) : (s.gatewayCount || 0)), 0);

                  return (
                    <div key={companyCode} className="gfm-summary-company" style={{ marginBottom: '8px' }}>
                      <div
                        className="gfm-summary-company-row"
                        style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', background: '#334155', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => setSummaryExpandedCompanies(prev => ({ ...prev, [companyCode]: !prev[companyCode] }))}
                      >
                        <span style={{ marginRight: '8px', fontSize: '10px', color: '#94a3b8' }}>{isCompanyExpanded ? '▼' : '▶'}</span>
                        <span style={{ fontWeight: 600, flex: 1, color: '#fff' }}>{companyCode}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>{storesToShow.length} stores / {totalGateways} GWs</span>
                      </div>
                      {isCompanyExpanded && (
                        <div className="gfm-summary-stores" style={{ marginLeft: '16px', marginTop: '4px' }}>
                          {storesToShow.map(store => {
                            const storeKey = `${companyCode}:${store.store}`;
                            const isStoreExpanded = summaryExpandedStores[storeKey];
                            const storeGateways = expandedStores[storeKey] || [];

                            return (
                              <div key={store.store} className="gfm-summary-store" style={{ marginBottom: '4px' }}>
                                <div
                                  className="gfm-summary-store-row"
                                  style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', background: '#475569', borderRadius: '4px', cursor: 'pointer' }}
                                  onClick={() => {
                                    if (!storeGateways.length && !loadingStores[storeKey]) {
                                      fetchStoreGateways(companyCode, store.store);
                                    }
                                    setSummaryExpandedStores(prev => ({ ...prev, [storeKey]: !prev[storeKey] }));
                                  }}
                                >
                                  <span style={{ marginRight: '8px', fontSize: '10px', color: '#cbd5e1' }}>
                                    {loadingStores[storeKey] ? '⏳' : (isStoreExpanded ? '▼' : '▶')}
                                  </span>
                                  <span style={{ flex: 1, fontSize: '12px', color: '#fff' }}>{store.store} - {store.storeName}</span>
                                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>{store.gatewayCount} GWs ({store.online || 0} online)</span>
                                </div>
                                {isStoreExpanded && storeGateways.length > 0 && (
                                  <div className="gfm-summary-gateways" style={{ marginLeft: '16px', marginTop: '4px' }}>
                                    {storeGateways.map(gw => (
                                      <div
                                        key={gw.mac}
                                        className="gfm-summary-gateway"
                                        style={{ display: 'flex', alignItems: 'center', padding: '3px 8px', fontSize: '11px', background: '#64748b', borderRadius: '3px', marginBottom: '2px', color: '#fff' }}
                                      >
                                        <span style={{ marginRight: '6px', color: gw.status === 'CONNECTED' ? '#4ade80' : '#94a3b8' }}>
                                          {gw.status === 'CONNECTED' ? '●' : '○'}
                                        </span>
                                        <span style={{ fontFamily: 'monospace', flex: 1, color: '#fff' }}>{gw.mac}</span>
                                        <span style={{ color: upgradeTargetFirmware && gw.version === upgradeTargetFirmware.version ? '#22c55e' : '#f59e0b', fontSize: '10px' }}>
                                          v{gw.version}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Bottom bar: Schedule + Start */}
        <div className="gfm-upgrade-bottombar">
          <label className="gfm-toolbar-radio"><input type="radio" name="scheduleType" checked={scheduleType === 'immediate'} onChange={() => setScheduleType('immediate')} />Now</label>
          <label className="gfm-toolbar-radio"><input type="radio" name="scheduleType" checked={scheduleType === 'scheduled'} onChange={() => setScheduleType('scheduled')} />Schedule</label>
          {scheduleType === 'scheduled' && (
            <>
              <input type="date" className="gfm-toolbar-input" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
              <input type="time" className="gfm-toolbar-input" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} />
            </>
          )}
          <input type="email" className="gfm-toolbar-input" placeholder="Notify email (optional)" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} style={{ width: '180px' }} />
          <button className="gfm-btn primary" onClick={startUpgrade} disabled={!upgradeTargetFirmware || upgradeSelectedCompanies.length === 0 || (scheduleType === 'scheduled' && (!scheduledDate || !scheduledTime))} style={{ marginLeft: 'auto' }}>
            {scheduleType === 'immediate' ? `Upgrade (${upgradeSelectedCompanies.length})` : `Schedule (${upgradeSelectedCompanies.length})`}
          </button>
        </div>
      </>
    );
  };

  // Render Jobs Tab (Active/Running Jobs)
  const renderJobs = () => (
    <>
      <div className="gfm-jobs-list">
        {upgradeJobs.length === 0 ? (
          <div className="gfm-empty">No active upgrade jobs found</div>
        ) : (
          upgradeJobs.map(job => {
            const companyCount = job.scope?.entries?.length || job.scope?.companies?.length || 0;
            return (
              <div key={job.jobId} className={`gfm-job-card ${activeJob?.jobId === job.jobId ? 'active' : ''}`} onClick={() => setActiveJob(job)}>
                <div className="gfm-job-header">
                  <code>{job.jobId}</code>
                  <StatusBadge status={job.status} small />
                </div>
                <div className="gfm-job-name">{job.name}</div>
                <div className="gfm-job-companies">
                  <span
                    className="gfm-job-companies-link"
                    onClick={(e) => { e.stopPropagation(); openJobScopeModal(job); }}
                    title="Click to view companies and stores"
                  >
                    {companyCount} {companyCount === 1 ? 'company' : 'companies'}
                  </span>
                </div>
                <div className="gfm-job-progress">
                  <div className="gfm-progress-bar">
                    <div className={`gfm-progress-fill ${job.status === 'completed' ? 'green' : 'blue'}`} style={{ width: `${job.progress?.percentComplete || 0}%` }} />
                  </div>
                  <span>{job.progress?.successful || 0}✓ / {job.progress?.failed || 0}✗ / {job.progress?.totalGateways || 0}</span>
                </div>
                {['running', 'scheduled'].includes(job.status) && (
                  <button className="gfm-btn danger small" onClick={(e) => { e.stopPropagation(); cancelJob(job.jobId); }}>Cancel</button>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );

  // Render History Tab (Completed/Cancelled/Failed Jobs)
  const renderHistory = () => (
    <>
      <div className="gfm-history-header">
        <h3>Upgrade Job History</h3>
        <span className="gfm-history-total">{historyPagination.total} total jobs</span>
      </div>

      {loadingHistory ? (
        <div className="gfm-loading"><div className="gfm-spinner"></div><p>Loading history...</p></div>
      ) : upgradeHistory.length === 0 ? (
        <div className="gfm-empty">No completed upgrade jobs found</div>
      ) : (
        <>
          <div className="gfm-history-table-wrapper">
            <table className="gfm-history-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Name</th>
                  <th>Target Firmware</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Companies</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {upgradeHistory.map(job => {
                  const duration = job.startedAt && job.completedAt
                    ? Math.round((new Date(job.completedAt) - new Date(job.startedAt)) / 1000 / 60)
                    : null;
                  const companyCount = job.scope?.entries?.length || job.scope?.companies?.length || 0;

                  return (
                    <tr key={job.jobId} className={`gfm-history-row status-${job.status}`}>
                      <td><code className="gfm-job-id-small">{job.jobId}</code></td>
                      <td className="gfm-history-name">{job.name}</td>
                      <td><span className="gfm-fw-badge">{job.targetFirmware?.version}</span></td>
                      <td><StatusBadge status={job.status} small /></td>
                      <td>
                        <div className="gfm-history-progress">
                          <span className="success">{job.progress?.successful || 0}✓</span>
                          <span className="failed">{job.progress?.failed || 0}✗</span>
                          <span className="total">/ {job.progress?.totalGateways || 0}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="gfm-job-companies-link"
                          onClick={() => openJobScopeModal(job)}
                          title="Click to view companies and stores"
                        >
                          {companyCount} {companyCount === 1 ? 'company' : 'companies'}
                        </span>
                      </td>
                      <td>{job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}</td>
                      <td>{job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}</td>
                      <td>{duration !== null ? `${duration} min` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {historyPagination.totalPages > 1 && (
            <div className="gfm-pagination">
              <button
                disabled={historyPagination.page <= 1}
                onClick={() => fetchUpgradeHistory(historyPagination.page - 1)}
              >
                ← Prev
              </button>
              <span>Page {historyPagination.page} of {historyPagination.totalPages}</span>
              <button
                disabled={historyPagination.page >= historyPagination.totalPages}
                onClick={() => fetchUpgradeHistory(historyPagination.page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </>
  );

  // Render Whitelist Modal
  const renderWhitelistModal = () => {
    // Get companies based on filter
    let modalCompanies = (summary?.companies || []).filter(c => c.whitelistEnabled !== null);
    if (whitelistModalFilter === 'enabled') {
      modalCompanies = modalCompanies.filter(c => c.whitelistEnabled === true);
    } else if (whitelistModalFilter === 'disabled') {
      modalCompanies = modalCompanies.filter(c => c.whitelistEnabled === false);
    }

    // Apply search filter
    if (whitelistModalSearch.trim()) {
      const search = whitelistModalSearch.toLowerCase().trim();
      modalCompanies = modalCompanies.filter(c =>
        c.code?.toLowerCase().includes(search) ||
        c.name?.toLowerCase().includes(search)
      );
    }

    const enabledCount = (summary?.companies || []).filter(c => c.whitelistEnabled === true).length;
    const disabledCount = (summary?.companies || []).filter(c => c.whitelistEnabled === false).length;

    return (
      <div className="gfm-modal-overlay" onClick={() => { setShowWhitelistModal(false); setWhitelistModalSearch(''); }}>
        <div className="gfm-modal large" onClick={e => e.stopPropagation()}>
          <div className="gfm-modal-header">
            <h2>🔒 Whitelist Status</h2>
            <span className="gfm-modal-count">{modalCompanies.length} companies</span>
            <button className="gfm-modal-close" onClick={() => { setShowWhitelistModal(false); setWhitelistModalSearch(''); }}>×</button>
          </div>

          <div className="gfm-modal-content">
            {/* Filters */}
            <div className="gfm-modal-filters">
              <input
                type="text"
                className="gfm-search-input"
                placeholder="Search companies..."
                value={whitelistModalSearch}
                onChange={(e) => setWhitelistModalSearch(e.target.value)}
              />
              <select
                value={whitelistModalFilter}
                onChange={(e) => setWhitelistModalFilter(e.target.value)}
                className="gfm-modal-filter-select"
              >
                <option value="all">All ({enabledCount + disabledCount})</option>
                <option value="enabled">Enabled ({enabledCount})</option>
                <option value="disabled">Disabled ({disabledCount})</option>
              </select>
            </div>

            {/* Bulk Actions */}
            {modalCompanies.length > 0 && (
              <div className="gfm-modal-bulk-actions">
                <span className="gfm-bulk-label">Bulk Actions ({modalCompanies.length} shown):</span>
                <button
                  className="gfm-btn success small"
                  onClick={() => bulkWhitelist(modalCompanies.filter(c => !c.whitelistEnabled), true)}
                  disabled={modalCompanies.filter(c => !c.whitelistEnabled).length === 0}
                >
                  Enable All ({modalCompanies.filter(c => !c.whitelistEnabled).length})
                </button>
                <button
                  className="gfm-btn danger small"
                  onClick={() => bulkWhitelist(modalCompanies.filter(c => c.whitelistEnabled), false)}
                  disabled={modalCompanies.filter(c => c.whitelistEnabled).length === 0}
                >
                  Disable All ({modalCompanies.filter(c => c.whitelistEnabled).length})
                </button>
              </div>
            )}

            {modalCompanies.length === 0 ? (
              <div className="gfm-empty">No companies match your filter</div>
            ) : (
              <div className="gfm-whitelist-list">
                {modalCompanies.map(company => (
                  <div key={company.code} className="gfm-whitelist-item">
                    <div className="gfm-whitelist-company">
                      <strong>{company.code}</strong>
                      <span>{company.name}</span>
                    </div>
                    <div className="gfm-whitelist-stats">
                      {company.gatewayCount !== null ? (
                        <>
                          <span>{company.storeCount} stores</span>
                          <span>{company.gatewayCount} gateways</span>
                          <span className="online">{company.online} online</span>
                        </>
                      ) : (
                        <span className="loading">Loading...</span>
                      )}
                    </div>
                    <button
                      className={`gfm-whitelist-btn ${company.whitelistEnabled ? 'enabled' : 'disabled'} small`}
                      onClick={() => { toggleWhitelist(company.code, !company.whitelistEnabled); }}
                    >
                      {company.whitelistEnabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Firmware Detail Modal
  const renderFirmwareModal = () => {
    const companies = getCompaniesWithVersion(selectedFirmwareVersion);
    const totalCount = summary?.firmwareVersions?.[selectedFirmwareVersion] || 0;
    const parsed = parseFirmwareVersion(selectedFirmwareVersion);

    return (
      <div className="gfm-modal-overlay" onClick={() => setShowFirmwareModal(false)}>
        <div className="gfm-modal large" onClick={e => e.stopPropagation()}>
          <div className="gfm-modal-header">
            <h2>
              <span>📡 Firmware Version</span>
              <span className="gfm-fw-version-badge">{selectedFirmwareVersion}</span>
            </h2>
            <span className="gfm-modal-count">{totalCount} gateways • {companies.length} companies</span>
            <button className="gfm-modal-close" onClick={() => setShowFirmwareModal(false)}>×</button>
          </div>

          <div className="gfm-modal-content">
            {companies.map(company => (
              <div key={company.code} className={`gfm-fw-company ${expandedCompanies[company.code] ? 'expanded' : ''}`}>
                <div
                  className="gfm-fw-company-header"
                  onClick={() => setExpandedCompanies(prev => ({ ...prev, [company.code]: !prev[company.code] }))}
                >
                  <span className="gfm-fw-expand">{expandedCompanies[company.code] ? '▼' : '▶'}</span>
                  <div className="gfm-fw-company-name">
                    {company.code}
                    <small>{company.name}</small>
                  </div>
                  <span className="gfm-fw-count">{company.versionCount} gateways</span>
                </div>

                {expandedCompanies[company.code] && company.storeDetails && (
                  <div className="gfm-fw-stores">
                    {company.storeDetails.filter(s => s.firmwareVersions?.[selectedFirmwareVersion] > 0).map(store => {
                      const storeKey = `${company.code}:${store.store}`;
                      const storeGateways = expandedStores[storeKey];
                      const isLoading = loadingStores[storeKey];
                      const isExpanded = !!storeGateways;

                      return (
                        <div key={store.store} className="gfm-fw-store-wrapper">
                          <div
                            className={`gfm-fw-store clickable ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => fetchStoreGateways(company.code, store.store, selectedFirmwareVersion)}
                          >
                            <span className="gfm-fw-store-expand">{isLoading ? '⏳' : (isExpanded ? '▼' : '▶')}</span>
                            <span className="gfm-fw-store-id">{store.store}</span>
                            <span className="gfm-fw-store-name">{store.storeName}</span>
                            <span className="gfm-fw-store-count">{store.firmwareVersions[selectedFirmwareVersion]} gw</span>
                          </div>
                          {isExpanded && storeGateways.length > 0 && (
                            <div className="gfm-fw-gateways">
                              {storeGateways.map(gw => (
                                <div key={gw.mac} className={`gfm-fw-gateway ${gw.status === 'CONNECTED' ? 'online' : 'offline'}`}>
                                  <span className="gfm-fw-gw-mac">{gw.mac}</span>
                                  <span className={`gfm-fw-gw-status ${gw.status === 'CONNECTED' ? 'online' : 'offline'}`}>
                                    {gw.status === 'CONNECTED' ? '●' : '○'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="gfm-container">
      <style>{`
        .gfm-upgrade-summary { color: #fff !important; }
        .gfm-summary-header { color: #fff !important; }
      `}</style>
      <div className="gfm-header">
        <h1>Gateway Firmware Manager</h1>
        <p>Manage firmware upgrades and whitelist across all companies</p>
      </div>

      {error && (
        <div className="gfm-error">{error}<button onClick={fetchSummary}>Retry</button></div>
      )}

      <div className="gfm-tabs">
        <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
        <button className={activeTab === 'upgrade' ? 'active' : ''} onClick={() => setActiveTab('upgrade')}>Push Upgrade</button>
        <button className={activeTab === 'jobs' ? 'active' : ''} onClick={() => { setActiveTab('jobs'); fetchUpgradeJobs(); }}>
          Upgrade Jobs {upgradeJobs.filter(j => j.status === 'running').length > 0 && <span className="gfm-badge">{upgradeJobs.filter(j => j.status === 'running').length}</span>}
        </button>
        <button className={activeTab === 'history' ? 'active' : ''} onClick={() => { setActiveTab('history'); fetchUpgradeHistory(); }}>
          History
        </button>

        {/* Data Source Selector */}
        <div className="gfm-data-source-selector">
          <select
            value={dataSource}
            onChange={(e) => handleDataSourceChange(e.target.value)}
            className="gfm-data-source-dropdown"
            title="This setting is persistent for all future loads"
          >
            <option value="live">Use Live Data</option>
            <option value="local">Use Local Data</option>
          </select>
          <span className="gfm-data-source-note">
            (persistent)
            {cacheStatus?.exists && dataSource === 'local' && (
              <span className="gfm-cache-age"> - cached {new Date(cacheStatus.cachedAt).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
            )}
          </span>
        </div>

        <button className="gfm-refresh-btn" onClick={() => { fetchSummary(true); fetchFirmwares(); }}>
          {loading ? '...' : '↻'} Refresh (Live Data)
        </button>

        <button className="gfm-refresh-btn" onClick={exportToExcel} disabled={!summary?.companies || !loadingProgress.done} title="Download gateway data as Excel">
          Export Excel
        </button>

        {loadingProgress.total > 0 && (
          <div className="gfm-progress-container">
            <span className="gfm-progress-label">Loading gateway data:</span>
            <div className="gfm-progress-track">
              <div className={`gfm-progress-bar-fill ${loadingProgress.done ? 'done' : ''}`} style={{ width: `${Math.round((loadingProgress.loaded / loadingProgress.total) * 100)}%` }} />
            </div>
            <span className={`gfm-progress-text ${loadingProgress.done ? 'done' : ''}`}>
              {loadingProgress.done ? `✓ ${loadingProgress.total}` : `${loadingProgress.loaded}/${loadingProgress.total}`}
            </span>
          </div>
        )}
      </div>

      {loading && !summary ? (
        <div className="gfm-loading"><div className="gfm-spinner"></div><p>Loading fleet data...</p></div>
      ) : (
        <div className={`gfm-content ${activeTab === 'dashboard' ? 'gfm-dashboard' : activeTab === 'upgrade' ? 'gfm-upgrade-tab' : activeTab === 'history' ? 'gfm-history' : 'gfm-jobs'}`}>
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'upgrade' && renderUpgrade()}
          {activeTab === 'jobs' && renderJobs()}
          {activeTab === 'history' && renderHistory()}
        </div>
      )}

      {showFirmwareModal && renderFirmwareModal()}
      {showWhitelistModal && renderWhitelistModal()}

      {/* Job Scope Modal - shows companies and stores for a job */}
      {showJobScopeModal && jobScopeData && (
        <div className="gfm-modal-overlay" onClick={() => setShowJobScopeModal(false)}>
          <div className="gfm-modal medium" onClick={e => e.stopPropagation()}>
            <div className="gfm-modal-header">
              <h2>📋 Job Scope</h2>
              <span className="gfm-modal-count">{jobScopeData.entries?.length || 0} companies</span>
              <button className="gfm-modal-close" onClick={() => setShowJobScopeModal(false)}>×</button>
            </div>
            <div className="gfm-modal-content">
              <div className="gfm-job-scope-info">
                <div className="gfm-scope-job-id"><strong>Job:</strong> <code>{jobScopeData.jobId}</code></div>
                <div className="gfm-scope-job-name"><strong>Name:</strong> {jobScopeData.name}</div>
              </div>
              <div className="gfm-job-scope-list">
                {(jobScopeData.entries || []).map((entry, idx) => {
                  // Match company to summary data for name lookup
                  const companyInfo = summary?.companies?.find(c => c.code === entry.company);
                  const stores = entry.stores === 'all' ? 'All Stores' :
                    Array.isArray(entry.stores) ? entry.stores.join(', ') : entry.stores;

                  return (
                    <div key={idx} className="gfm-scope-company-item">
                      <div className="gfm-scope-company-header">
                        <strong>{entry.company}</strong>
                        {companyInfo?.name && <span className="gfm-scope-company-name">{companyInfo.name}</span>}
                      </div>
                      <div className="gfm-scope-stores">
                        <span className="gfm-scope-stores-label">Stores:</span>
                        <span className="gfm-scope-stores-value">{stores}</span>
                      </div>
                    </div>
                  );
                })}
                {(!jobScopeData.entries || jobScopeData.entries.length === 0) && (
                  <div className="gfm-empty">No companies in this job scope</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Detail Modal - shows gateway type breakdown when clicking company code */}
      {showCompanyDetailModal && selectedCompanyDetail && (() => {
        const company = selectedCompanyDetail;
        const firmwareVersions = company.firmwareVersions || {};

        // Group firmware versions by gateway type
        const byType = {};
        Object.entries(firmwareVersions).forEach(([version, count]) => {
          const parsed = parseFirmwareVersion(version);
          if (!byType[parsed.type]) byType[parsed.type] = { total: 0, versions: {} };
          byType[parsed.type].total += count;
          byType[parsed.type].versions[version] = count;
        });

        // Sort function for versions based on current sort state
        const sortVersions = (versions) => {
          const entries = Object.entries(versions);
          if (companyModalSort.by === 'version') {
            entries.sort((a, b) => {
              const cmp = compareFirmwareVersions(a[0], b[0]);
              return companyModalSort.dir === 'asc' ? cmp : -cmp;
            });
          } else {
            // Sort by count
            entries.sort((a, b) => companyModalSort.dir === 'asc' ? a[1] - b[1] : b[1] - a[1]);
          }
          return entries.map(([version, count]) => ({ version, count }));
        };

        // Sort types by custom order
        const typeOrder = { 'Newton Gateway': 1, 'Newton USB Gateway': 2, 'Newton PCIe Gateway': 3, 'Newton Cisco USB Gateway': 4, 'Unknown': 99 };
        const sortedTypes = Object.entries(byType)
          .map(([type, data]) => ({
            type,
            total: data.total,
            versions: sortVersions(data.versions)
          }))
          .sort((a, b) => (typeOrder[a.type] || 50) - (typeOrder[b.type] || 50));

        const totalGateways = sortedTypes.reduce((sum, t) => sum + t.total, 0);

        // Toggle sort handler
        const toggleSort = (sortBy) => {
          setCompanyModalSort(prev => {
            if (prev.by === sortBy) {
              return { by: sortBy, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
            }
            return { by: sortBy, dir: 'desc' };
          });
        };

        return (
          <div className="gfm-modal-overlay" onClick={() => setShowCompanyDetailModal(false)}>
            <div className="gfm-modal large" onClick={e => e.stopPropagation()}>
              <div className="gfm-modal-header">
                <h2><span className="company-code">{company.code}</span> {company.name}</h2>
                <button className="gfm-modal-close" onClick={() => setShowCompanyDetailModal(false)}>×</button>
              </div>
              <div className="gfm-modal-content">
                {/* Summary stats */}
                <div className="gfm-company-modal-stats">
                  <div className="gfm-stat-box">
                    <span className="gfm-stat-value">{company.storeCount || 0}</span>
                    <span className="gfm-stat-label">Stores</span>
                  </div>
                  <div className="gfm-stat-box">
                    <span className="gfm-stat-value">{totalGateways.toLocaleString()}</span>
                    <span className="gfm-stat-label">Gateways</span>
                  </div>
                  <div className="gfm-stat-box">
                    <span className="gfm-stat-value online">{company.online || 0}</span>
                    <span className="gfm-stat-label">Online</span>
                  </div>
                  <div className="gfm-stat-box">
                    <span className="gfm-stat-value offline">{company.offline || 0}</span>
                    <span className="gfm-stat-label">Offline</span>
                  </div>
                </div>

                {/* Sort controls */}
                <div className="gfm-modal-sort-controls">
                  <span className="gfm-sort-label">Sort by:</span>
                  <button
                    className={`gfm-sort-btn ${companyModalSort.by === 'count' ? 'active' : ''}`}
                    onClick={() => toggleSort('count')}
                  >
                    Count {companyModalSort.by === 'count' && (companyModalSort.dir === 'desc' ? '↓' : '↑')}
                  </button>
                  <button
                    className={`gfm-sort-btn ${companyModalSort.by === 'version' ? 'active' : ''}`}
                    onClick={() => toggleSort('version')}
                  >
                    Version {companyModalSort.by === 'version' && (companyModalSort.dir === 'desc' ? '↓' : '↑')}
                  </button>
                </div>

                {/* Gateway type breakdown columns */}
                <div className="gfm-fw-columns">
                  {sortedTypes.map(({ type, total, versions }) => (
                    <div key={type} className="gfm-fw-column">
                      <div className="gfm-fw-column-header">
                        <span className="gfm-fw-type-name">{type}</span>
                        <span className="gfm-fw-type-total">{total.toLocaleString()}</span>
                      </div>
                      <div className="gfm-fw-column-body">
                        {versions.slice(0, 10).map(({ version, count }) => {
                          const percent = ((count / total) * 100).toFixed(2);
                          const isTarget = targetVersion && version.includes(targetVersion.replace(/^N/, ''));
                          return (
                            <div key={version} className={`gfm-fw-item ${isTarget ? 'target' : ''}`}>
                              <span className="gfm-fw-version">{version.startsWith('N') ? version : `N${version}`}</span>
                              <span className={`gfm-fw-pill ${isTarget ? 'green' : ''}`}>{count.toLocaleString()} ({percent}%)</span>
                            </div>
                          );
                        })}
                        {versions.length > 10 && (
                          <div className="gfm-fw-item muted">+{versions.length - 10} more versions</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {sortedTypes.length === 0 && (
                    <div className="gfm-empty">No gateway data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default GatewayFleetManager;
